/** CCTV 렌즈·해상도 → 화각(자동) */

export const DEFAULT_RESOLUTION_W = 1920;
export const DEFAULT_RESOLUTION_H = 1080;
export const DEFAULT_FOCAL_LENGTH_MM = 2.8;
export const DEFAULT_SENSOR_WIDTH_MM = 5.37;

export type MountType = 'ceiling' | 'wall';

export const RESOLUTION_PRESETS: { label: string; w: number; h: number }[] = [
  { label: '720p (1280×720)', w: 1280, h: 720 },
  { label: '1080p (1920×1080)', w: 1920, h: 1080 },
  { label: '1440p (2560×1440)', w: 2560, h: 1440 },
  { label: '4K (3840×2160)', w: 3840, h: 2160 },
];

const mountBaseRangeM: Record<MountType, number> = {
  ceiling: 25,
  wall: 35,
};

export function focalFromHorizontalFovDeg(hfovDeg: number, sensorWidthMm: number): number {
  const rad = (Math.max(20, Math.min(140, hfovDeg)) * Math.PI) / 180;
  return sensorWidthMm / (2 * Math.tan(rad / 2));
}

export function focalFromVerticalFovDeg(
  vfovDeg: number,
  sensorWidthMm: number,
  resolutionW: number,
  resolutionH: number,
): number {
  const aspect = resolutionH / Math.max(resolutionW, 1);
  const vRad = (Math.max(15, Math.min(120, vfovDeg)) * Math.PI) / 180;
  const hRad = 2 * Math.atan(Math.tan(vRad / 2) / aspect);
  return sensorWidthMm / (2 * Math.tan(hRad / 2));
}

export type CameraOpticsInput = {
  mount_type?: MountType | string;
  resolution_w?: number;
  resolution_h?: number;
  focal_length_mm?: number;
  sensor_width_mm?: number;
  /** 지정 시 초점 대신 수평 화각 우선 */
  fov_h_deg?: number;
  /** 지정 시(수평 미지정) 세로 화각으로 초점 역산 */
  fov_v_deg?: number;
  max_range_m?: number;
};

export type CameraOpticsResult = {
  resolution_w: number;
  resolution_h: number;
  focal_length_mm: number;
  sensor_width_mm: number;
  fov_h_deg: number;
  fov_v_deg: number;
  max_range_m: number;
};

export function applyCameraOptics(cam: CameraOpticsInput): CameraOpticsResult {
  const mount: MountType = cam.mount_type === 'wall' ? 'wall' : 'ceiling';
  let rw = Math.floor(Number(cam.resolution_w) || DEFAULT_RESOLUTION_W);
  let rh = Math.floor(Number(cam.resolution_h) || DEFAULT_RESOLUTION_H);
  rw = Math.max(320, Math.min(8192, rw));
  rh = Math.max(240, Math.min(8192, rh));
  let sensorW = Number(cam.sensor_width_mm) || DEFAULT_SENSOR_WIDTH_MM;
  sensorW = Math.max(3, Math.min(12, sensorW));
  const aspect = rh / Math.max(rw, 1);

  const explicitH =
    cam.fov_h_deg != null && Number.isFinite(Number(cam.fov_h_deg)) && Number(cam.fov_h_deg) > 0;
  const explicitV =
    cam.fov_v_deg != null && Number.isFinite(Number(cam.fov_v_deg)) && Number(cam.fov_v_deg) > 0;

  let focal = Number(cam.focal_length_mm) || DEFAULT_FOCAL_LENGTH_MM;
  let hfovDeg: number;
  let vfovDeg: number;

  const hasFocal =
    cam.focal_length_mm != null &&
    Number.isFinite(Number(cam.focal_length_mm)) &&
    Number(cam.focal_length_mm) > 0;

  // 초점 우선: 초점이 있으면 H/V는 센서 비율로만 파생 (실제 렌즈와 동일)
  if (hasFocal) {
    focal = Math.max(1.8, Math.min(50, Number(cam.focal_length_mm)));
    const hfovRad = 2 * Math.atan(sensorW / (2 * focal));
    hfovDeg = (hfovRad * 180) / Math.PI;
    vfovDeg = (2 * Math.atan(Math.tan(hfovRad / 2) * aspect) * 180) / Math.PI;
  } else if (explicitH) {
    hfovDeg = Math.max(20, Math.min(140, Number(cam.fov_h_deg)));
    focal = focalFromHorizontalFovDeg(hfovDeg, sensorW);
    vfovDeg = explicitV
      ? Math.max(15, Math.min(120, Number(cam.fov_v_deg)))
      : (2 * Math.atan(Math.tan((hfovDeg * Math.PI) / 180 / 2) * aspect) * 180) / Math.PI;
  } else if (explicitV) {
    vfovDeg = Math.max(15, Math.min(120, Number(cam.fov_v_deg)));
    focal = focalFromVerticalFovDeg(vfovDeg, sensorW, rw, rh);
    const hfovRad = 2 * Math.atan(sensorW / (2 * focal));
    hfovDeg = (hfovRad * 180) / Math.PI;
  } else {
    focal = Math.max(1.8, Math.min(50, focal));
    const hfovRad = 2 * Math.atan(sensorW / (2 * focal));
    hfovDeg = (hfovRad * 180) / Math.PI;
    vfovDeg = (2 * Math.atan(Math.tan(hfovRad / 2) * aspect) * 180) / Math.PI;
  }

  focal = Math.max(1.8, Math.min(50, focal));
  const hClamped = Math.max(20, Math.min(140, hfovDeg));
  const vClamped = Math.max(15, Math.min(120, vfovDeg));

  const baseRange = mountBaseRangeM[mount];
  const refPixels = DEFAULT_RESOLUTION_W * DEFAULT_RESOLUTION_H;
  const scale = Math.sqrt((rw * rh) / refPixels);
  let maxRangeM = Number(cam.max_range_m);
  if (!Number.isFinite(maxRangeM) || maxRangeM <= 0) {
    maxRangeM = Math.max(5, Math.min(60, baseRange * scale));
  } else {
    maxRangeM = Math.max(5, Math.min(60, maxRangeM));
  }

  return {
    resolution_w: rw,
    resolution_h: rh,
    focal_length_mm: Math.round(focal * 100) / 100,
    sensor_width_mm: Math.round(sensorW * 100) / 100,
    fov_h_deg: Math.round(hClamped * 100) / 100,
    fov_v_deg: Math.round(vClamped * 100) / 100,
    max_range_m: Math.round(maxRangeM * 10) / 10,
  };
}

/** 2.8mm + 1/2.8" + 1080p 기준 업계 흔한 광각(≈87.6°×56.7°) */
export function layoutDefaultOptics(
  focalMm = DEFAULT_FOCAL_LENGTH_MM,
  resolutionW = DEFAULT_RESOLUTION_W,
  resolutionH = DEFAULT_RESOLUTION_H,
): CameraOpticsResult {
  return applyCameraOptics({
    focal_length_mm: focalMm,
    resolution_w: resolutionW,
    resolution_h: resolutionH,
    sensor_width_mm: DEFAULT_SENSOR_WIDTH_MM,
  });
}

export const DEFAULT_FOV_H_DEG = layoutDefaultOptics().fov_h_deg;
export const DEFAULT_FOV_V_DEG = layoutDefaultOptics().fov_v_deg;
